import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { ListingCard, NamedLink } from '../../components';

import css from './SectionOtherListingsMaybe.module.css';

const CARD_RENDER_SIZES = [
  '(max-width: 767px) 70vw',
  '(max-width: 1023px) 38vw',
  '(max-width: 1439px) 26vw',
  '260px',
].join(', ');

const SCROLL_EDGE_TOLERANCE = 4;

/**
 * "More offers from this provider" — full-width horizontal card rail below the
 * listing content, linking to the author's other published listings.
 *
 * @param {Object} props
 * @param {Array<Object>} props.listings - other listings by the same author (denormalized)
 * @param {Object} props.author - ensured author user entity
 * @param {Object} [props.intl] - optional, avoids an extra hook call when the parent has it
 * @returns {JSX.Element|null}
 */
const SectionOtherListingsMaybe = props => {
  const { listings = [], author } = props;
  const intlFromContext = useIntl();
  const intl = props.intl || intlFromContext;
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_TOLERANCE);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_TOLERANCE);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, listings.length]);

  const scrollByPage = direction => () => {
    const el = scrollerRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.9) * direction;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (listings.length === 0 || !author?.id) {
    return null;
  }

  return (
    <section className={css.root}>
      <div className={css.wrapper}>
        <div className={css.header}>
          <h2 className={css.title}>
            <FormattedMessage id="ListingPage.otherListingsTitle" />
          </h2>
          <NamedLink name="ProfilePage" params={{ id: author.id.uuid }} className={css.viewAll}>
            <span className={css.viewAllLabel}>
              <FormattedMessage id="ListingPage.otherListingsViewAll" />
            </span>
            <ChevronRight className={css.viewAllIcon} aria-hidden="true" />
          </NamedLink>
        </div>
        <div className={css.scrollerWrap}>
          <button
            type="button"
            className={classNames(css.navButton, css.navButtonLeft, {
              [css.navButtonHidden]: !canScrollLeft,
            })}
            aria-label={intl.formatMessage({ id: 'ListingPage.otherListingsScrollPrev' })}
            onClick={scrollByPage(-1)}
            tabIndex={canScrollLeft ? 0 : -1}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            className={classNames(css.navButton, css.navButtonRight, {
              [css.navButtonHidden]: !canScrollRight,
            })}
            aria-label={intl.formatMessage({ id: 'ListingPage.otherListingsScrollNext' })}
            onClick={scrollByPage(1)}
            tabIndex={canScrollRight ? 0 : -1}
          >
            <ChevronRight aria-hidden="true" />
          </button>
          <div className={css.scroller} ref={scrollerRef}>
            <ul
              className={css.list}
              aria-label={intl.formatMessage({ id: 'ListingPage.otherListingsTitle' })}
            >
              {listings.map(listing => (
                <li key={listing.id.uuid} className={css.cardWrap}>
                  <div className={css.cardShell}>
                    <ListingCard
                      className={css.listingCard}
                      listing={listing}
                      renderSizes={CARD_RENDER_SIZES}
                      intl={intl}
                      showAuthorInfo={false}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionOtherListingsMaybe;
